/**
 * A wrapper for iframe video embeds.
 *
 * @example
 * 	<video-embed>
 * 		<iframe src="{YouTube or Vimeo embed URL}" />
 * 		(or <video src="{video URL}" />)
 * 		<button type="button">Play video</button>
 * 	</video-embed>
 */

import VimeoPlayer from "@vimeo/player";
import { vimeoService } from "../services.common/vimeoService";
import { youtubeService } from "../services.common/youtubeService";

export class VideoEmbed extends HTMLElement {
	public static NAME = "video-embed";

	#button: HTMLButtonElement;
	#iframe: HTMLIFrameElement;
	#video: HTMLVideoElement;
	#ytPlayer: Promise<YT.Player>;
	#vimeoPlayer: Promise<VimeoPlayer>;
	#intersectionObserver: IntersectionObserver;
	#started: boolean = false;

	#paused = async () => {
		if (this.#video) {
			return new Promise<boolean>((resolve) => {
				resolve(this.#video.paused);
			});
		} else if (this.#ytPlayer) {
			return this.#ytPlayer.then((player) => {
				const state = player.getPlayerState();
				return state === 0 || state === 2 || state === 5;
			});
		} else if (this.#vimeoPlayer) {
			return this.#vimeoPlayer.then((player) => {
				return player.getPaused();
			});
		}
	};

	#handleStateChange = (paused: boolean) => {
		if (!this.#started && this.#button && paused) {
			this.dataset.state = "ready";
		} else if (paused) {
			this.dataset.state = "paused";
		} else {
			this.#started = true;
			this.dataset.state = "playing";

			// const rect = this.getBoundingClientRect();
			// if (rect.top < 0 || rect.bottom > window.innerHeight) {
			// 	this.scrollIntoView({
			// 		block: "center",
			// 	});
			// }
		}
	};

	#handleIntersection = async (entries: IntersectionObserverEntry[]) => {
		if (entries[0].isIntersecting) {
			this.#intersectionObserver.disconnect();

			if (this.#iframe?.dataset.src) {
				this.#iframe.src = this.#iframe.dataset.src;
			}

			this.#initPlayer();

			if (this.#video?.autoplay) {
				this.play();
				this.#video.addEventListener(
					"timeupdate",
					this.#handleTimeUpdate,
				);
			}
		}
	};

	#handleTimeUpdate = () => {
		if (this.#video.currentTime >= 5) {
			this.pause();
			this.#video.removeEventListener(
				"timeupdate",
				this.#handleTimeUpdate,
			);
		}
	};

	#initEventListeners = () => {
		if (this.#video) {
			this.#video.addEventListener("pause", () => {
				this.#handleStateChange(true);
			});
			this.#video.addEventListener("play", () => {
				this.#handleStateChange(false);
			});
		} else if (this.#ytPlayer) {
			this.#ytPlayer.then((player) => {
				player.addEventListener(
					"onStateChange",
					({
						data,
					}: YT.PlayerEvent & {
						data: YT.PlayerState;
					}) => {
						this.#handleStateChange(
							data === 0 || data === 2 || data === 5,
						);
					},
				);
			});
		} else if (this.#vimeoPlayer) {
			this.#vimeoPlayer.then((player) => {
				player.on("pause", () => {
					this.#handleStateChange(true);
				});
				player.on("play", () => {
					this.#handleStateChange(false);
				});
			});
		}

		this.#button?.addEventListener("click", this.toggle);
	};

	#initPlayer = async () => {
		if (this.#iframe?.src.indexOf("youtube") > -1) {
			this.#ytPlayer = youtubeService.register(this.#iframe);
		} else if (this.#iframe?.src.indexOf("vimeo") > -1) {
			this.#vimeoPlayer = vimeoService.register(this.#iframe);
		}

		this.#handleStateChange(await this.#paused());

		this.#initEventListeners();
	};

	connectedCallback(): void {
		this.#button = this.querySelector(":scope > button");
		this.#iframe = this.querySelector(":scope > iframe");
		this.#video = this.querySelector(":scope > video");

		this.#handleStateChange(true);

		this.#intersectionObserver = new IntersectionObserver(
			this.#handleIntersection,
			{
				root: null,
				rootMargin: "0px 0px 200px 0px",
				threshold: 0,
			},
		);

		this.#intersectionObserver.observe(this);

		// For overriding the title generated by the YouTube API (for YouTube Video media type)
		if (this.#iframe) {
			const customTitle = this.#iframe.dataset.customTitle;
			if (customTitle) {
				// Listen for iframe load event
				this.#iframe.addEventListener("load", () => {
					this.#iframe.setAttribute("title", customTitle);
				});

				// If the iframe is already loaded for some reason, force the update
				if (this.#iframe.src && this.#iframe.contentDocument) {
					this.#iframe.setAttribute("title", customTitle);
				}
			}
		}

		if (this.#video?.autoplay) {
			this.pause();
		}
	}

	disconnectedCallback(): void {
		this.#button?.removeEventListener("click", this.toggle);
		this.#video?.removeEventListener("timeupdate", this.#handleTimeUpdate);
		this.#ytPlayer?.then((player) => {
			player.destroy();
		});
		this.#vimeoPlayer?.then((player) => {
			player.destroy();
		});
		this.#intersectionObserver?.disconnect();
	}

	toggle = async (): Promise<void> => {
		const paused = await this.#paused();
		if (paused) {
			this.play();
		} else {
			this.pause();
		}
	};

	play = (): void => {
		if (this.#video) {
			this.#video.removeAttribute("tabindex");
			this.#video.removeAttribute("inert");
			this.#video.play();
		} else if (this.#ytPlayer) {
			this.#ytPlayer.then((player) => {
				this.#iframe.removeAttribute("tabindex");
				this.#iframe.removeAttribute("inert");
				this.#iframe.focus();
				player.playVideo();
			});
		} else if (this.#vimeoPlayer) {
			this.#vimeoPlayer.then((player) => {
				this.#iframe.removeAttribute("tabindex");
				this.#iframe.removeAttribute("inert");
				this.#iframe.focus();
				player.play();
			});
		}

		if (!this.#video?.autoplay) {
			this.#button?.remove();
		}
	};

	pause = (): void => {
		if (this.#video) {
			this.#video.pause();
		} else if (this.#ytPlayer) {
			this.#ytPlayer.then((player) => {
				player.pauseVideo();
			});
		} else if (this.#vimeoPlayer) {
			this.#vimeoPlayer.then((player) => {
				player.pause();
			});
		}
	};

	rewind = (): void => {
		if (this.#video) {
			this.#video.pause();
			this.#video.currentTime = 0;
		} else if (this.#ytPlayer) {
			this.#ytPlayer.then((player) => {
				player.seekTo(0, false);
				player.pauseVideo();
			});
		} else if (this.#vimeoPlayer) {
			this.#vimeoPlayer.then((player) => {
				player.setCurrentTime(0);
				player.pause();
			});
		}
	};
}

customElements.define(VideoEmbed.NAME, VideoEmbed);
